import { useCallback, useEffect, useState } from 'react';

/**
 * A router, in forty lines.
 *
 * There are now three kinds of URL here — the public site, the dashboard, and
 * the invitation link — and they have to survive being reloaded, bookmarked
 * and pasted to a colleague. That is a router. What it is not is a reason to
 * add a routing library: this application has no nested layouts, no loaders,
 * no data router, and no route-level code splitting that a manual chunk would
 * not express better.
 *
 * `pushState` does not fire `popstate` — that event is the *browser's* history
 * movement, not ours — so a navigation dispatches its own event. Missing that
 * is why a hand-rolled router usually works on the back button and not on
 * links.
 */

const NAVIGATION = 'gsi:navigation';

/** Where the browser is, re-rendered when that changes. */
export function useLocation(): { readonly path: string; readonly search: string } {
  const read = (): { path: string; search: string } => ({
    // Trailing slashes are stripped so `/app/` and `/app` are one route, and
    // the root stays `/` rather than collapsing to an empty string.
    path: window.location.pathname.replace(/(.)\/+$/, '$1'),
    search: window.location.search,
  });

  const [location, setLocation] = useState(read);

  useEffect(() => {
    const update = (): void => {
      setLocation(read());
    };
    window.addEventListener('popstate', update);
    window.addEventListener(NAVIGATION, update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener(NAVIGATION, update);
    };
  }, []);

  return location;
}

export function navigate(path: string, options: { readonly replace?: boolean } = {}): void {
  if (options.replace === true) window.history.replaceState(null, '', path);
  else window.history.pushState(null, '', path);
  window.dispatchEvent(new Event(NAVIGATION));
  // A new page starts at the top. Without this, opening a call from row forty
  // of a list shows the detail screen scrolled past its own heading.
  window.scrollTo(0, 0);
}

/**
 * Is this URL ours to handle in the page?
 *
 * Anything with a scheme, a target, or a modifier key held down belongs to the
 * browser. Swallowing a cmd-click is the single most irritating thing a
 * client-side router does.
 */
export function useLinkHandler(): (
  href: string,
  event: React.MouseEvent<HTMLAnchorElement>,
) => void {
  return useCallback((href, event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !href.startsWith('/')
    ) {
      return;
    }
    event.preventDefault();
    navigate(href);
  }, []);
}
